/**
 * NativeSubscriptionGate — shown inside the iOS app when a coach's account
 * has no active subscription. Offers the Apple In-App Purchase (StoreKit via
 * RevenueCat) so iOS users can subscribe in-app, which Apple requires (3.1.1).
 *
 * Web coaches still subscribe on the website (Stripe) and simply log in — they
 * never see this screen. Anyone who subscribed previously can Restore.
 *
 * Visual treatment matches the marketing landing page's dark hero so the screen
 * feels native; it's one of the first screens Apple's reviewer sees.
 */
import React, { useEffect, useState } from "react";
import { LogOut, Check, Loader2 } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { getMonthlyOffer, purchaseMonthly, restoreIAP } from "../../utils/iap";

const TERMS_URL = "https://platform.byanymeanscoaches.com/#/terms";
const PRIVACY_URL = "https://platform.byanymeanscoaches.com/#/privacy";

const PERKS = [
  "Full drill & masterclass library",
  "Practice & playbook builders",
  "AI plan generator + community",
];

export function NativeSubscriptionGate({ userEmail, onSignOut, onSubscribed }) {
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(null); // "buy" | "restore" | null
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getMonthlyOffer().then((o) => { if (!cancelled) setOffer(o); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const priceLabel = offer?.priceString || "$39.99";

  const subscribe = async () => {
    if (busy) return;
    setErr(null);
    if (!offer?.product) {
      setErr("Couldn't load the subscription from the App Store yet. Please try again in a few minutes.");
      return;
    }
    setBusy("buy");
    try {
      const ok = await purchaseMonthly(offer.product);
      if (ok) onSubscribed?.();
    } catch {
      setErr("That purchase couldn't be completed. Please try again.");
    } finally { setBusy(null); }
  };

  const restore = async () => {
    if (busy) return;
    setErr(null); setBusy("restore");
    try {
      const ok = await restoreIAP();
      if (ok) onSubscribed?.();
      else setErr("No active subscription was found to restore.");
    } finally { setBusy(null); }
  };

  return (
    <div style={{
      minHeight: "100vh", width: "100%", background: "#0A0A0A", color: "#F5F5F5",
      fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
      paddingTop: "max(40px, env(safe-area-inset-top))",
      paddingBottom: "max(28px, env(safe-area-inset-bottom))",
      paddingLeft: 24, paddingRight: 24,
    }}>
      <div aria-hidden="true" style={{
        position: "absolute", top: "10%", left: "50%", transform: "translateX(-50%)",
        width: 540, height: 540, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(226,221,159,0.12) 0%, rgba(226,221,159,0.03) 45%, transparent 70%)",
        filter: "blur(40px)", pointerEvents: "none",
      }}/>

      <div style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        textAlign: "center", maxWidth: 460, margin: "0 auto", position: "relative", zIndex: 1, width: "100%",
      }}>
        <div style={{ position: "relative", marginBottom: 22 }}>
          <div aria-hidden="true" style={{
            position: "absolute", inset: -18, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(226,221,159,0.20), transparent 65%)", filter: "blur(14px)",
          }}/>
          <img src={BAM_LOGO_PNG} alt="BAM" style={{
            width: 66, height: 66, objectFit: "contain", position: "relative",
            filter: "drop-shadow(0 0 24px rgba(226,221,159,0.45))",
          }}/>
        </div>

        <div style={{ fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>
          BAM Coaches Platform
        </div>
        <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, letterSpacing: 2.5, color: "#F5F5F5", margin: "0 0 18px", lineHeight: 1.02 }}>
          Unlock full access
        </h1>

        {/* Perks */}
        <div style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 10, marginBottom: 26, textAlign: "left" }}>
          {PERKS.map((p) => (
            <div key={p} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#CFCFCF" }}>
              <span style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(226,221,159,0.14)", border: `1px solid ${GOLD}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Check size={11} color={GOLD}/>
              </span>
              {p}
            </div>
          ))}
        </div>

        {/* Subscribe */}
        <button onClick={subscribe} disabled={!!busy}
          style={{
            width: "100%", maxWidth: 340, padding: "15px 22px", borderRadius: 12, border: "none",
            background: GOLD, color: "#111", fontSize: 16, fontWeight: 800, fontFamily: "inherit",
            letterSpacing: 0.3, cursor: busy ? "default" : "pointer", opacity: busy === "restore" ? 0.6 : 1,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            boxShadow: `0 10px 28px -10px ${GOLD}66`,
          }}>
          {busy === "buy" ? <><Loader2 size={16} className="spin"/> Processing…</> : <>Subscribe · {priceLabel}/mo</>}
        </button>

        {err && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: "#F87171", maxWidth: 340 }}>{err}</div>
        )}

        {/* Legal — required for auto-renewable subscriptions */}
        <p style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.5, marginTop: 16, maxWidth: 340 }}>
          Auto-renewing subscription. {priceLabel}/month billed to your Apple Account. Renews automatically unless cancelled at least 24 hours before the period ends; manage or cancel in your device Settings. By subscribing you agree to our{" "}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#9CA3AF", textDecoration: "underline" }}>Terms of Use</a> and{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#9CA3AF", textDecoration: "underline" }}>Privacy Policy</a>.
        </p>

        {/* Restore + sign out */}
        <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 22 }}>
          <button onClick={restore} disabled={!!busy}
            style={{ background: "transparent", border: "none", color: "#9CA3AF", fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: busy ? "default" : "pointer" }}>
            {busy === "restore" ? "Restoring…" : "Restore Purchases"}
          </button>
          <span style={{ width: 1, height: 14, background: "rgba(255,255,255,0.12)" }}/>
          <button onClick={onSignOut} disabled={!!busy}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "#9CA3AF", fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
            <LogOut size={13}/> Sign out
          </button>
        </div>

        {userEmail && (
          <div style={{ fontSize: 11.5, color: "#555", marginTop: 16, letterSpacing: 0.2 }}>
            Signed in as <span style={{ color: "#9CA3AF" }}>{userEmail}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default NativeSubscriptionGate;
